import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type { Board, CustomMetric, Report, Source, Space, User } from 'webapp/models-custom';

interface Args {
	model: Board | Source | User | CustomMetric | Space | Report;
	type: string;
	agencyLimitations?: Partial<
		Record<
			| 'mustDeleteClients'
			| 'mustDeleteUsers'
			| 'mustDeleteSources'
			| 'mustDeleteDatasets'
			| 'mustDeleteBoards'
			| 'mustDeleteCustomMetrics'
			| 'mustDeleteReports',
			unknown
		>
	>; // when type === 'client', each truthy when that limit is breached
}

export default class BillingDowngradeRowComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked confirmActiveOnId = '';

	get isBoardType() {
		return this.args.type === 'board';
	}

	get isSourceType() {
		return this.args.type === 'source';
	}

	get isUserType() {
		return this.args.type === 'user';
	}

	get isCustomMetricType() {
		return this.args.type === 'customMetric';
	}

	get isClientType() {
		return this.args.type === 'client';
	}

	get isReportType() {
		return this.args.type === 'report';
	}

	get avatar() {
		let avatar = '';
		if (this.isSourceType) {
			const model = this.args.model as Source;
			avatar = '<span class="avatar"><img src="' + model.realAvatar + '" /></span>';
		} else if (this.isBoardType) {
			const model = this.args.model as Board;
			avatar =
				'<span class="avatar color" style="background-color:' + model.color + '"></span>';
		} else if (this.isCustomMetricType) {
			const model = this.args.model as CustomMetric;
			avatar =
				'<span class="avatar"><img src="' +
				model.usedSources[0]?.realAvatar +
				'" /></span>';
		} else if (this.isReportType) {
			const model = this.args.model as Report;
			avatar = '<span class="avatar"><img src="' + model.image + '" /></span>';
		}
		return htmlSafe(avatar);
	}

	get title() {
		let title = '';
		if (this.isSourceType) {
			const model = this.args.model as Source;
			title = model.title;
		} else if (this.isUserType || this.isBoardType) {
			const model = this.args.model as User | Board;
			title = model.name;
		} else if (this.isCustomMetricType) {
			const model = this.args.model as CustomMetric;
			title = model.metricName;
		} else if (this.isClientType) {
			const model = this.args.model as Space;
			title = model.companyName;
		} else if (this.isReportType) {
			const model = this.args.model as Report;
			title = model.title;
		}
		return title;
	}

	get subtitle() {
		let title = '';
		if (this.isSourceType) {
			const model = this.args.model as Source;
			title = model.subtitle;
		} else if (this.isBoardType) {
			const model = this.args.model as Board;
			title = model.sources.map((source) => source.description).join(', ');
		} else if (this.isUserType) {
			const model = this.args.model as User;
			title = model.email;
		} else if (this.isClientType) {
			const model = this.args.model as Space;
			const {
				userCount,
				sourceCount,
				datasetCount,
				boardCount,
				customMetricCount,
				reportCount,
			} = model;
			const {
				mustDeleteUsers,
				mustDeleteSources,
				mustDeleteDatasets,
				mustDeleteBoards,
				mustDeleteCustomMetrics,
				mustDeleteReports,
			} = this.args.agencyLimitations || {};
			const warningClass = 'warning';

			title += `<span${userCount > 0 && mustDeleteUsers ? ' class="' + warningClass + '"' : ''}>${userCount} users</span>, `;
			title += `<span${sourceCount > 0 && mustDeleteSources ? ' class="' + warningClass + '"' : ''}>${sourceCount} data sources</span>, `;
			title += `<span${datasetCount > 0 && mustDeleteDatasets ? ' class="' + warningClass + '"' : ''}>${datasetCount} datasets</span>, `;
			title += `<span${boardCount > 0 && mustDeleteBoards ? ' class="' + warningClass + '"' : ''}>${boardCount} dashboards</span>, `;
			title += `<span${customMetricCount > 0 && mustDeleteCustomMetrics ? ' class="' + warningClass + '"' : ''}>${customMetricCount} custom metrics</span>, `;
			title += `<span${reportCount > 0 && mustDeleteReports ? ' class="' + warningClass + '"' : ''}>${reportCount} reports</span>.`;
		}
		return title;
	}

	get canDelete() {
		if (this.isClientType && !this.args.agencyLimitations?.mustDeleteClients) {
			return false;
		}
		if (this.isUserType) {
			const model = this.args.model as User;
			return !model.isMe && !model.isAccountOwner;
		}
		return true;
	}

	get modelAsUser() {
		return this.args.model as User;
	}

	get modelAsClient() {
		return this.args.model as Space;
	}

	get showLoginButton() {
		return this.isClientType && !this.confirmActiveOnId;
	}

	@action
	activateConfirm() {
		this.confirmActiveOnId = this.args.model.id!;
	}

	@action
	confirmYes() {
		this.delete();
		this.deactivateConfirm();
	}

	@action
	deactivateConfirm() {
		this.confirmActiveOnId = '';
	}

	@action
	onModelChange() {
		if (this.confirmActiveOnId && this.confirmActiveOnId !== this.args.model.id) {
			this.deactivateConfirm();
		}
	}

	@action
	delete() {
		const model = this.args.model;
		let modelTypeName = '';

		if (this.isSourceType) {
			modelTypeName = `Data source ${(model as Source).title}`;
		} else if (this.isBoardType) {
			modelTypeName = `Dashboard ${(model as Board).name}`;
		} else if (this.isClientType) {
			modelTypeName = 'Account';
		} else if (this.isCustomMetricType) {
			modelTypeName = `Custom metric ${(model as CustomMetric).metricName}`;
		} else if (this.isUserType) {
			modelTypeName = `User ${(model as User).name}`;
		} else if (this.isReportType) {
			modelTypeName = `Report ${(model as Report).title}`;
		}

		const layer = this.infolayerService.loading(`Deleting ${modelTypeName}...`),
			successMessage = `${modelTypeName} deleted!`,
			asyncType = this.isSourceType ? 'sources' : this.isUserType ? 'users' : '';
		if (asyncType) {
			// async delete
			this.requestService
				.apiWithAsyncReply('DELETE', `/${asyncType}/${model.id}`)
				.then(
					() => {
						layer.ok(successMessage);
						void model.delete(true).catch();
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		} else if (this.isBoardType) {
			// handle board deletion with permanent parameter
			this.requestService
				.api('DELETE', `/boards/${model.id}?permanent=true`)
				.then(
					() => {
						layer.ok(successMessage);
						void model.delete(true).catch();
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		} else if (this.isReportType) {
			// handle board deletion with permanent parameter
			this.requestService
				.api('DELETE', `/reports/${model.id}?permanent=true`)
				.then(
					() => {
						layer.ok(successMessage);
						void model.delete(true).catch();
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		} else {
			model
				.delete()
				.then(
					() => {
						layer.ok(successMessage);
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}
	<template>
		<li
			class="table-row {{if this.confirmActiveOnId 'confirm bgRed'}}"
			{{didUpdate this.onModelChange @model}}
		>
			{{#if this.isUserType}}
				<AvatarOrInitialsComponent
					@name={{this.modelAsUser.name}}
					@avatar={{this.modelAsUser.avatar}}
				/>
			{{else if this.isClientType}}
				<AvatarOrInitialsComponent @name={{this.modelAsClient.companyName}} />
			{{else}}
				{{{this.avatar}}}
			{{/if}}

			<span class="title">{{this.title}}</span>
			{{{this.subtitle}}}

			<div class="buttons">
				{{#if this.showLoginButton}}
					<a
						href={{this.modelAsClient.loginUrl}}
						class="button login size-small theme-blue-outlined"
					>
						Login
					</a>
				{{/if}}
				{{#if this.canDelete}}
					{{#if this.confirmActiveOnId}}
						<a role="button" class="confirm-no" {{on "click" this.deactivateConfirm}}>
							Cancel
						</a>
						<a role="button" class="confirm-yes" {{on "click" this.confirmYes}}>
							<CommonUiIconComponent @name="Trash" @size="sm" />
							<span class="underline">Confirm</span>
						</a>
					{{else}}
						<a role="button" class="del" {{on "click" this.activateConfirm}}>
							<CommonUiIconComponent @name="Trash" @size="sm" />
						</a>
					{{/if}}
				{{/if}}
			</div>
		</li>
	</template>
}
